"use client";

import type { FC, ReactNode } from "react";
import { useState } from "react";
import { BreadcrumbNavigation, type BreadcrumbItem } from "@/components/breadcrumb-navigation";
import { AddressAutocompleteInput, type LocationSelection } from "@/features/maps";
import { cn } from "@/lib/cn";
import { routes } from "@/lib/routes";
import { HeroCollage, HeroCollageStrip } from "./hero-collage";
import { HeroSwirl } from "./hero-swirl";

const DEFAULT_BREADCRUMB: BreadcrumbItem[] = [
  { label: "Home", href: routes.cmwHome.url },
  { label: "Find a Practitioner" },
];

const DEFAULT_TITLE = "Find a practitioner who understands you";
const DEFAULT_DESCRIPTION =
  "Browse more than 110+ certified practitioners and find the right match for your wellbeing journey.";
const DEFAULT_DESCRIPTION_MOBILE = "Browse from 110+ verified practitioners nationally";

type DirectorySearchHeroProps = {
  breadcrumb?: BreadcrumbItem[];
  title?: string;
  description?: string;
  descriptionMobile?: string;
  defaultValue?: string;
  location?: LocationSelection | null;
  inputId?: string;
  onLocationChange?: (selection: LocationSelection) => void;
  onClear?: () => void;
  resultLabel?: ReactNode;
  className?: string;
};

export const DirectorySearchHero: FC<DirectorySearchHeroProps> = ({
  breadcrumb = DEFAULT_BREADCRUMB,
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  descriptionMobile = DEFAULT_DESCRIPTION_MOBILE,
  defaultValue = "",
  location: controlledLocation,
  inputId,
  onLocationChange,
  onClear,
  resultLabel,
  className,
}) => {
  const [ownLocation, setOwnLocation] = useState(defaultValue);
  const location = controlledLocation !== undefined ? (controlledLocation?.address ?? "") : ownLocation;

  const handleLocationChange = (selection: LocationSelection) => {
    setOwnLocation(selection.address);
    onLocationChange?.(selection);
  };

  const handleClear = () => {
    setOwnLocation("");
    onClear?.();
  };

  return (
    <section
      aria-labelledby="directory-search-hero-title"
      className={cn(
        "relative overflow-hidden bg-tone-aqua-tint pb-32 lg:pb-64",
        "pt-[calc(var(--public-header-height)+24px)] lg:pt-[calc(var(--public-header-height)+64px)]",
        className,
      )}
    >
      <HeroSwirl className="pointer-events-none absolute top-[calc(var(--public-header-height)-15px)] left-[calc(50%-277.5px)] h-432 w-full -translate-x-1/2 lg:top-[calc(var(--public-header-height)+5px)] lg:left-auto lg:right-0 lg:h-602 lg:w-full lg:translate-x-0" />
      <HeroCollage />

      <div className="page-width relative flex flex-col gap-24 lg:gap-32">
        <div className="flex w-full flex-col gap-24 lg:max-w-1/2 min-[1440px]:max-w-[70%]">
          <div className="flex flex-col gap-16 lg:gap-24">
            <BreadcrumbNavigation items={breadcrumb} />
            <h1 id="directory-search-hero-title" className="text-28 font-semibold leading-[1.3] text-neutral-black lg:text-44">
              {title}
            </h1>
            <p className="text-18 leading-[1.2] tracking-[-0.18px] text-neutral-black lg:hidden">{descriptionMobile}</p>
            <p className="hidden text-18 leading-[1.2] tracking-[-0.18px] text-neutral-black lg:block lg:text-21 lg:tracking-normal">
              {description}
            </p>
          </div>

          <AddressAutocompleteInput
            id={inputId}
            defaultValue={defaultValue}
            value={controlledLocation !== undefined ? location : undefined}
            onLocationChange={handleLocationChange}
            onClear={handleClear}
          />

          {location && (
            <p aria-live="polite" className="text-16 leading-[1.4] text-neutral-black/50 lg:text-18">
              Search showing{" "}
              <span className="italic text-brand-dark-blue">{resultLabel ?? `Practitioner in ${location}`}</span>
            </p>
          )}
        </div>
      </div>

      <HeroCollageStrip className="mt-24" />
    </section>
  );
};
